/**
 * UI Component Colors
 * 
 * Centralized color definitions for reusable UI components
 */

// Page Header Colors
export const PAGE_HEADER_COLORS = {
  // Light mode gradient
  gradientFrom: 'from-primary-50',
  gradientTo: 'to-blue-50',
  
  // Dark mode gradient
  darkGradientFrom: 'dark:from-gray-800',
  darkGradientTo: 'dark:to-gray-900',
  
  // Border
  border: 'border-primary-100',
  darkBorder: 'dark:border-gray-700',
  
  // Combined gradient
  gradient: 'bg-gradient-to-r from-primary-50 to-blue-50 dark:from-gray-800 dark:to-gray-900',
  
  // Combined border
  borderColor: 'border-primary-100 dark:border-gray-700'
}

// Card Colors
export const CARD_COLORS = {
  background: 'bg-white dark:bg-gray-800',
  border: 'border-gray-200 dark:border-gray-700',
  hover: 'hover:border-gray-300 dark:hover:border-gray-600'
}

// Sidebar Colors
export const SIDEBAR_COLORS = {
  background: 'bg-white dark:bg-gray-800',
  border: 'border-r border-gray-200 dark:border-gray-700',
  itemHover: 'hover:bg-gray-100 dark:hover:bg-gray-700',
  itemActive: 'bg-primary-50 dark:bg-primary-900/20'
}

// Top Navigation Colors
export const TOPNAV_COLORS = {
  background: 'bg-white dark:bg-gray-800',
  border: 'border-b border-gray-200 dark:border-gray-700',
  text: 'text-gray-900 dark:text-white'
}

// Modal Colors
export const MODAL_COLORS = {
  overlay: 'bg-gray-900/50 dark:bg-black/70',
  background: 'bg-white dark:bg-gray-800',
  border: 'border-gray-200 dark:border-gray-700',
  header: 'bg-gray-50 dark:bg-gray-900'
}

// Input Colors
export const INPUT_COLORS = {
  background: 'bg-white dark:bg-gray-800',
  border: 'border-gray-300 dark:border-gray-600',
  focusBorder: 'focus:border-indigo-500 focus:ring-indigo-500',
  text: 'text-gray-900 dark:text-white',
  placeholder: 'placeholder-gray-400 dark:placeholder-gray-500',
  disabled: 'bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 cursor-not-allowed',
  readonly: 'bg-gray-50 dark:bg-gray-900',
  error: {
    border: 'border-red-300 dark:border-red-500',
    focus: 'focus:border-red-500 focus:ring-red-500',
    text: 'text-red-600 dark:text-red-400'
  },
  icon: 'text-gray-400 dark:text-gray-500',
  iconHover: 'text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400'
}

// Button Colors (in addition to Button component)
export const BUTTON_BASE_COLORS = {
  primary: {
    bg: 'bg-primary-600 dark:bg-primary-500',
    hover: 'hover:bg-primary-700 dark:hover:bg-primary-600',
    text: 'text-white'
  },
  secondary: {
    bg: 'bg-gray-600 dark:bg-gray-700',
    hover: 'hover:bg-gray-700 dark:hover:bg-gray-600',
    text: 'text-white'
  },
  success: {
    bg: 'bg-green-600 dark:bg-green-500',
    hover: 'hover:bg-green-700 dark:hover:bg-green-600',
    text: 'text-white'
  },
  danger: {
    bg: 'bg-red-600 dark:bg-red-500',
    hover: 'hover:bg-red-700 dark:hover:bg-red-600',
    text: 'text-white'
  }
}

// Text Colors (for Typography components)
export const TEXT_COLORS = {
  default: {
    light: 'text-gray-900',
    dark: 'dark:text-white',
    combined: 'text-gray-900 dark:text-white'
  },
  primary: {
    light: 'text-primary-600',
    dark: 'dark:text-primary-400',
    combined: 'text-primary-600 dark:text-primary-400'
  },
  secondary: {
    light: 'text-gray-700',
    dark: 'dark:text-gray-300',
    combined: 'text-gray-700 dark:text-gray-300'
  },
  muted: {
    light: 'text-gray-500',
    dark: 'dark:text-gray-400',
    combined: 'text-gray-500 dark:text-gray-400'
  },
  accent: {
    light: 'text-primary-700',
    dark: 'dark:text-primary-300',
    combined: 'text-primary-700 dark:text-primary-300'
  },
  success: {
    light: 'text-green-600',
    dark: 'dark:text-green-400',
    combined: 'text-green-600 dark:text-green-400'
  },
  warning: {
    light: 'text-yellow-600',
    dark: 'dark:text-yellow-400',
    combined: 'text-yellow-600 dark:text-yellow-400'
  },
  danger: {
    light: 'text-red-600',
    dark: 'dark:text-red-400',
    combined: 'text-red-600 dark:text-red-400'
  },
  info: {
    light: 'text-blue-600',
    dark: 'dark:text-blue-400',
    combined: 'text-blue-600 dark:text-blue-400'
  },
  // Legacy aliases for backward compatibility
  link: 'text-primary-600 dark:text-primary-400'
}

// Background Colors
export const BG_COLORS = {
  primary: 'bg-gray-50 dark:bg-gray-900',
  secondary: 'bg-gray-100 dark:bg-gray-800',
  card: 'bg-white dark:bg-gray-800',
  hover: 'hover:bg-gray-50 dark:hover:bg-gray-700'
}

// Confirmation Dialog Icon Colors
export const CONFIRMATION_ICON_COLORS = {
  warning: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    text: 'text-yellow-600 dark:text-yellow-400',
    combined: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
  },
  danger: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-600 dark:text-red-400',
    combined: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
  },
  info: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-600 dark:text-blue-400',
    combined: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
  },
  success: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-600 dark:text-green-400',
    combined: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
  },
  question: {
    bg: 'bg-gray-100 dark:bg-gray-700',
    text: 'text-gray-600 dark:text-gray-400',
    combined: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
  }
}

// Badge Colors
export const BADGE_COLORS = {
  default: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  primary: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  secondary: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  success: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  danger: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  info: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  gray: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  cyan: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
  yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  green: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  red: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  indigo: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  pink: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200'
}

// Button Colors
export const BUTTON_COLORS = {
  primary: 'bg-primary-600 dark:bg-primary-500 text-white hover:bg-primary-700 dark:hover:bg-primary-600 focus:ring-primary-500 disabled:bg-primary-300 dark:disabled:bg-primary-800 disabled:cursor-not-allowed shadow-sm',
  secondary: 'bg-gray-600 dark:bg-gray-500 text-white hover:bg-gray-700 dark:hover:bg-gray-600 focus:ring-gray-500 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed shadow-sm',
  outline: 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm',
  danger: 'bg-red-600 dark:bg-red-500 text-white hover:bg-red-700 dark:hover:bg-red-600 focus:ring-red-500 disabled:bg-red-300 dark:disabled:bg-red-800 disabled:cursor-not-allowed shadow-sm',
  success: 'bg-green-600 dark:bg-green-500 text-white hover:bg-green-700 dark:hover:bg-green-600 focus:ring-green-500 disabled:bg-green-300 dark:disabled:bg-green-800 disabled:cursor-not-allowed shadow-sm',
  warning: 'bg-yellow-600 dark:bg-yellow-500 text-white hover:bg-yellow-700 dark:hover:bg-yellow-600 focus:ring-yellow-500 disabled:bg-yellow-300 dark:disabled:bg-yellow-800 disabled:cursor-not-allowed shadow-sm',
  ghost: 'bg-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:ring-gray-500 disabled:text-gray-400 dark:disabled:text-gray-600 disabled:cursor-not-allowed',
  link: 'bg-transparent text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:underline focus:ring-0 disabled:text-gray-400 dark:disabled:text-gray-600 disabled:cursor-not-allowed'
}

// Tag Colors
export const TAG_COLORS = {
  default: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  primary: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  success: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  warning: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
  danger: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  info: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300'
}

// User Role Badge Colors
export const ROLE_BADGE_COLORS = {
  super_admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  owner: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  user: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
}

// Notification Colors
export const NOTIFICATION_COLORS = {
  badge: 'bg-red-500 text-white',
  unreadBg: 'bg-blue-50',
  unreadDot: 'bg-indigo-500',
  link: 'text-indigo-600 hover:text-indigo-500'
}

// Tooltip Colors
export const TOOLTIP_COLORS = {
  dark: 'bg-gray-900 dark:bg-gray-700',
  light: 'bg-white border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100',
  primary: 'bg-primary-600 dark:bg-primary-500',
  success: 'bg-green-600 dark:bg-green-500',
  warning: 'bg-yellow-600 dark:bg-yellow-500',
  danger: 'bg-red-600 dark:bg-red-500'
}

// Link Colors
export const LINK_COLORS = {
  primary: 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300',
  secondary: 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white',
  muted: 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300',
  danger: 'text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300',
  success: 'text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300',
  unstyled: ''
}

// Empty State Colors
export const EMPTY_STATE_COLORS = {
  default: 'text-gray-400 dark:text-gray-500',
  search: 'text-gray-400 dark:text-gray-500',
  error: 'text-red-400 dark:text-red-500',
  success: 'text-green-400 dark:text-green-500',
  empty: 'text-gray-400 dark:text-gray-500'
}

// Avatar Colors
export const AVATAR_COLORS = {
  primary: 'bg-primary-600 dark:bg-primary-500',
  secondary: 'bg-gray-600 dark:bg-gray-500',
  success: 'bg-green-600 dark:bg-green-500',
  danger: 'bg-red-600 dark:bg-red-500',
  warning: 'bg-yellow-600 dark:bg-yellow-500',
  info: 'bg-blue-600 dark:bg-blue-500',
  purple: 'bg-purple-600 dark:bg-purple-500',
  pink: 'bg-pink-600 dark:bg-pink-500',
  indigo: 'bg-indigo-600 dark:bg-indigo-500'
}

// Error Page Colors
export const ERROR_PAGE_COLORS = {
  background: 'bg-gray-50 dark:bg-gray-900',
  iconBackground: 'bg-gradient-to-br from-primary-50 to-blue-50 dark:from-gray-800 dark:to-gray-700',
  iconBorder: 'border-2 border-primary-100 dark:border-gray-600',
  icon: 'text-primary-500 dark:text-primary-400',
  errorCode: 'text-gray-900 dark:text-white',
  title: 'text-gray-900 dark:text-white',
  description: 'text-gray-600 dark:text-gray-400',
  divider: 'border-gray-200 dark:border-gray-700',
  helpText: 'text-gray-500 dark:text-gray-400',
  linkText: 'text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300'
}

// Form Colors
export const FORM_COLORS = {
  divider: 'border-gray-200 dark:border-gray-700'
}

// Theme Icon Colors
export const THEME_ICON_COLORS = {
  sun: 'text-yellow-500 dark:text-yellow-400',
  moon: 'text-indigo-500 dark:text-indigo-400'
}

// Reports & Analytics Colors
export const REPORT_COLORS = {
  // Tabs
  tab: {
    active: {
      border: 'border-primary-500',
      text: 'text-primary-600 dark:text-primary-400'
    },
    inactive: {
      border: 'border-transparent',
      text: 'text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
    },
    badge: {
      active: 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300',
      inactive: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
    }
  },
  
  // Filter Section
  filter: {
    background: 'bg-white dark:bg-gray-800',
    border: 'border-gray-200 dark:border-gray-600',
    divider: 'border-gray-200 dark:border-gray-600',
    text: {
      primary: 'text-gray-900 dark:text-white',
      secondary: 'text-gray-700 dark:text-gray-300',
      muted: 'text-gray-500 dark:text-gray-400'
    },
    button: {
      primary: 'bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white',
      secondary: 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
    },
    input: {
      background: 'bg-white dark:bg-gray-700',
      border: 'border-gray-300 dark:border-gray-600',
      text: 'text-gray-900 dark:text-white',
      placeholder: 'placeholder-gray-400 dark:placeholder-gray-500',
      focus: 'focus:ring-primary-500 focus:border-primary-500'
    }
  },
  
  // Charts
  chart: {
    background: 'bg-white dark:bg-gray-800',
    border: 'border-gray-200 dark:border-gray-700',
    text: 'text-gray-900 dark:text-white',
    emptyState: {
      icon: 'text-gray-400 dark:text-gray-600',
      text: 'text-gray-500 dark:text-gray-400'
    }
  },
  
  // Date Picker
  datePicker: {
    background: 'bg-white dark:bg-gray-800',
    border: 'border-gray-300 dark:border-gray-600',
    hover: 'hover:bg-gray-100 dark:hover:bg-gray-700',
    selected: 'bg-primary-600 text-white',
    today: 'border-primary-500 text-primary-600 dark:text-primary-400',
    disabled: 'text-gray-400 dark:text-gray-600 cursor-not-allowed',
    header: 'text-gray-900 dark:text-white',
    dayName: 'text-gray-700 dark:text-gray-300'
  },
  
  // Avatar
  avatar: {
    background: 'bg-gray-200 dark:bg-gray-700',
    text: 'text-gray-700 dark:text-gray-300',
    border: 'border-2 border-white dark:border-gray-800',
    overflow: 'bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-400 dark:hover:bg-gray-500',
    colorScheme: {
      primary: '#3B82F6',
      secondary: '#6B7280',
      success: '#10B981',
      warning: '#F59E0B',
      danger: '#EF4444'
    },
    colorPalette: [
      '#3B82F6', // blue
      '#10B981', // green
      '#F59E0B', // amber
      '#EF4444', // red
      '#8B5CF6', // purple
      '#EC4899', // pink
      '#14B8A6', // teal
      '#F97316'  // orange
    ]
  }
}

// Date Range Picker Colors
export const DATE_RANGE_COLORS = {
  preset: {
    active: 'bg-primary-600 text-white',
    inactive: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
  },
  input: {
    background: 'bg-white dark:bg-gray-700',
    border: 'border-gray-300 dark:border-gray-600',
    text: 'text-gray-900 dark:text-white',
    placeholder: 'placeholder-gray-400 dark:placeholder-gray-500'
  },
  display: {
    background: 'bg-primary-50 dark:bg-primary-900/20',
    border: 'border-primary-200 dark:border-primary-800',
    icon: 'text-primary-600 dark:text-primary-400'
  }
}

